import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Shield } from 'lucide-react';
import axios from 'axios';
import { PLANS } from '../data/plans';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  message?: string;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ open, onClose, message }) => {
  const [selected, setSelected] = useState<'lecteur' | 'collection'>('lecteur');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const joinWaitlist = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/users/plan-waitlist', { plan: selected });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Impossible d’enregistrer votre demande pour le moment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <button className="absolute inset-0 bg-ink/40" aria-label="Fermer" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-card p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-ink-muted hover:text-ink">
          <X className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2 text-forest-600 mb-3">
          <Shield className="h-5 w-5" />
          <span className="text-xs font-semibold tracking-widest uppercase">Offre Découverte utilisée</span>
        </div>
        <h2 className="font-serif text-2xl text-ink mb-2">Votre emprunt gratuit a été utilisé</h2>
        <p className="text-sm text-ink-muted leading-relaxed mb-5">
          {message ||
            'Le compte gratuit permet un seul emprunt, pour essayer le parcours jusqu’au bout. Les lectures suivantes passent par un abonnement, à un tarif volontairement bas.'}
        </p>

        {submitted ? (
          <div className="rounded-xl bg-forest-50 border border-forest-100 p-4 text-sm text-forest-800">
            Vous êtes sur la liste d’ouverture de l’offre {selected === 'lecteur' ? 'Lecteur' : 'Collection'}.
            Nous vous écrirons dès que le paiement sera actif.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {PLANS.filter((p) => p.id !== 'free').map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setSelected(plan.id as 'lecteur' | 'collection')}
                  className={`text-left rounded-xl border p-3 transition-colors ${
                    selected === plan.id
                      ? 'border-forest-600 bg-forest-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-medium text-ink">{plan.name}</p>
                  <p className="text-sm text-ink-muted">
                    {plan.price} {plan.period}
                  </p>
                </button>
              ))}
            </div>
            {error && <p className="text-sm text-red-700 mb-3">{error}</p>}
            <button
              type="button"
              onClick={joinWaitlist}
              disabled={loading}
              className="btn btn-primary w-full mb-3"
            >
              {loading ? 'Enregistrement…' : 'Prévenir-moi à l’ouverture'}
            </button>
            <Link to="/#tarifs" onClick={onClose} className="block text-center text-sm text-forest-700 hover:underline">
              Voir le détail des offres
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default UpgradeModal;
